import {
  AppButton,
  AppCard,
  AppIconButton,
  AppMemberDisplay,
  AppTextarea,
  AppTooltip,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Avatar, AvatarFallback } from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { getCommentError } from '@opportunities/-lib/opportunityValidation'
import { Edit2, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FIELD_MAX_LENGTHS } from 'shared'
import { toast } from 'sonner'
import { formatDate } from '@/lib/format-utils'
import { cn } from '@/lib/utils'
import type { CommentWithNumber } from '../types'
import { handleCommentChange } from '../utils'
import { LinkifiedText } from './LinkifiedText'

export interface CommentItemProps {
  comment: CommentWithNumber
  editable: boolean
  currentMemberId: number | null
  isAdministrator: boolean
  onUpdate: (commentId: number, comment: string) => Promise<void>
  onDelete: (commentId: number) => Promise<void>
  isUpdating?: boolean
}

/**
 * コメントアイテムコンポーネント
 *
 * 個別コメントの表示・編集・削除を担当
 */
export function CommentItem({
  comment,
  editable,
  currentMemberId,
  isAdministrator,
  onUpdate,
  onDelete,
  isUpdating = false,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedComment, setEditedComment] = useState(comment.comment)
  const [editError, setEditError] = useState<string | null>(null)

  const isOwnComment = currentMemberId === comment.created_by
  const canEdit = editable && isOwnComment && !comment.disabled_at
  const canDelete = editable && (isOwnComment || isAdministrator) && !comment.disabled_at
  const isEdited = comment.created_at !== comment.updated_at

  const handleUpdate = async () => {
    const error = getCommentError(editedComment)
    if (error) {
      toast.error(error)
      return
    }
    await onUpdate(comment.id, editedComment)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    await onDelete(comment.id)
  }

  const handleCancelEdit = () => {
    setEditedComment(comment.comment)
    setEditError(null)
    setIsEditing(false)
  }

  useEffect(() => {
    setEditedComment(comment.comment)
  }, [comment.comment])

  // 削除済みコメント
  if (comment.disabled_at) {
    return (
      <div className={cn('flex gap-3 p-3', isOwnComment ? 'flex-row-reverse' : 'flex-row')}>
        <Avatar className='h-8 w-8 shrink-0'>
          <AvatarFallback className='text-xs bg-gray-300'>-</AvatarFallback>
        </Avatar>
        <div className={cn('min-w-0 max-w-[80%] bg-muted/50 rounded-lg p-3', isOwnComment ? 'ml-auto' : 'mr-auto')}>
          <p className='text-sm text-muted-foreground italic'>このコメントは削除済みです</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex gap-3 p-3', isOwnComment ? 'flex-row-reverse' : 'flex-row')}>
      <Avatar className='h-8 w-8 shrink-0'>
        <AvatarFallback className='text-xs bg-primary/10'>{comment.created_by_name?.slice(0, 2) || '?'}</AvatarFallback>
      </Avatar>
      <AppCard
        asContent={false}
        className={cn(
          'min-w-0 max-w-[60%] rounded-lg overflow-hidden py-0 gap-0',
          isOwnComment ? 'ml-auto' : 'mr-auto',
        )}
      >
        <div className='flex items-center justify-between gap-2 px-3 py-2 bg-muted'>
          <div className='flex items-center gap-2 text-sm'>
            <span className='font-medium'>{comment.commentNumber} :</span>
            <AppMemberDisplay employeeNumber={comment.created_by_employee_number} name={comment.created_by_name} />
            <AppTooltip content={formatDate(comment.updated_at, 'datetime')}>
              <span className='text-muted-foreground cursor-help'>
                {formatDate(comment.updated_at, 'relative')}
                {isEdited && <span className='ml-1'>（編集済み）</span>}
              </span>
            </AppTooltip>
          </div>
          {(canEdit || canDelete) && !isEditing && (
            <div className='flex items-center gap-1'>
              {canEdit && (
                <AppIconButton description='コメント編集' className='h-6 w-6 p-0' onClick={() => setIsEditing(true)}>
                  <Edit2 />
                </AppIconButton>
              )}
              {canDelete && (
                <AppIconButton description='コメント削除' className='h-6 w-6 p-0' onClick={handleDelete}>
                  <Trash2 />
                </AppIconButton>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className='p-3 space-y-2'>
            <AppTextarea
              value={editedComment}
              onChange={(e) => handleCommentChange(e.target.value, setEditedComment, setEditError)}
              rows={2}
              disabled={isUpdating}
              maxLength={FIELD_MAX_LENGTHS.COMMENT}
              showCharCount
              error={editError ?? undefined}
            />
            <div className='flex justify-end gap-2'>
              <AppButton variant='outline' size='sm' onClick={handleCancelEdit} disabled={isUpdating}>
                キャンセル
              </AppButton>
              <AppButton variant='save' size='sm' onClick={handleUpdate} disabled={isUpdating}>
                保存
              </AppButton>
            </div>
          </div>
        ) : (
          <div className='px-3 py-2 text-sm whitespace-pre-wrap wrap-break-word'>
            <LinkifiedText text={comment.comment} />
          </div>
        )}
      </AppCard>
    </div>
  )
}
